<template>
  <div class="card-maquina" :style="cardBorderStyle">
    <div class="side-status-bar" :style="{ background: colorEstatus(item.status) }"></div>
    <div class="card-header">
      <div class="machine-id-block">
        <div class="machine-label-row">
          <v-icon size="13" :color="colorEstatus(item.status)" class="mr-1 machine-cog-icon">mdi-cog</v-icon>
          <span class="machine-label">Máquina</span>
        </div>
        <span class="machine-code">{{ item.codigoMaquina }}</span>
      </div>
      <!-- Molde entre la máquina y la etiqueta de estado: solo el código, sin la palabra "Molde". -->
      <span v-if="item.codigoMolde" class="header-molde">{{ item.codigoMolde }}</span>
      <div class="header-right">
        <div class="status-pill" :style="pillStyle">
          <span class="status-led" :style="{ background: colorEstatus(item.status) }"></span>
          {{ labelEstatus(item.status) }}
        </div>
        <div v-if="item.activaSinOt" class="activa-badge">
          <span class="activa-led"></span>
          Activa sin OT
        </div>
        <!-- OT abierta pero el contador no la reporta dando golpes: se clasifica como Sin OT. -->
        <div v-if="item.otSinGolpes" class="activa-badge badge-gray">
          <span class="activa-led"></span>
          OT sin golpes
        </div>
      </div>
    </div>

    <template v-if="!isInactive">

      <div v-if="showOeeSection" class="oee-section">
        <div class="ring-wrap">
          <svg viewBox="0 0 90 90" width="90" height="90" style="transform:rotate(-90deg)">
            <circle cx="45" cy="45" r="36" fill="none" stroke="#F0F2F6" stroke-width="7"/>
            <circle
              cx="45" cy="45" r="36" fill="none"
              :stroke="colorEstatus(item.status)"
              stroke-width="7"
              stroke-linecap="round"
              :stroke-dasharray="`${oeeArc} ${circumference}`"
            />
          </svg>
          <div class="ring-center">
            <template v-if="item.oee != null">
              <ICountUp class="ring-value" :endVal="item.oee" :options="{ decimalPlaces: 0 }" />
              <span class="ring-pct">%</span>
            </template>
            <template v-else>
              <span class="ring-value">—</span>
            </template>
            <div class="ring-sub">OEE</div>
          </div>
        </div>

        <div class="kpi-trio">
          <div class="kpi-row" v-for="k in kpis" :key="k.name">
            <div class="kpi-label-block">
              <div class="kpi-dot" :style="{ background: k.color }"></div>
              <span class="kpi-name">{{ k.name }}</span>
            </div>
            <div class="kpi-bar-wrap">
              <div class="kpi-bar-fill" :style="{ width: k.val + '%', background: k.color }"></div>
            </div>
            <span class="kpi-value">{{ k.display }}</span>
          </div>
        </div>
      </div>

      <!-- TC CHIPS. En la versión compacta se omite el TC Cotizado y el de planeación
           se rotula "TC Programado". Si está activa sin OT, solo muestra TC Real. -->
      <div v-if="showTcRow" class="tc-row">
        <div v-if="!item.activaSinOt" class="tc-chip">
          <span class="tc-chip-label">{{ compacta ? 'TC Programado' : 'TC Planeación' }}</span>
          <span class="tc-chip-value qad">{{ tcDisplay(item.tcProgramadoTxt) }}</span>
        </div>
        <div class="tc-chip">
          <span class="tc-chip-label">TC Real</span>
          <span v-if="item.tcRealCargando" class="tc-chip-value">
            <v-progress-circular indeterminate size="12" width="2" color="#8A95A3" />
          </span>
          <span v-else class="tc-chip-value">{{ tcDisplay(item.tcRealTxt) }}</span>
        </div>
        <div v-if="!compacta && !item.activaSinOt" class="tc-chip">
          <span class="tc-chip-label">TC Cotizado</span>
          <span class="tc-chip-value">{{ tcDisplay(item.tcRecetaTxt) }}</span>
        </div>
      </div>

      <div class="h-divider"></div>

      <div class="info-section">
        <template v-if="item.status >= 1 && nombreEmpresa === 'TAMTO'">
          <div class="info-row">
            <span class="info-key">Artículo</span>
            <span class="info-val">{{ item.nombreArticulo }}</span>
          </div>
          <div class="info-row">
            <span class="info-key">OT</span>
            <span class="info-val ot-val">{{ limpiarOT(item.ot) }}</span>
          </div>
        </template>

        <div v-if="showCavidades" class="cavidades-block">
          <div
            v-for="(ca, i) in item.cavidadesArticulos"
            :key="'cav-'+i"
            class="cavidades-item"
          >
            <span class="cavidades-dot"></span>
            <span class="cavidades-art">{{ ca.nombre }}{{ sufijoLado(ca.lado) }}</span>
            <span v-if="ca.meta" class="cavidades-meta">{{ metaTxt(ca.meta) }} pzas</span>
            <span class="cavidades-badge">{{ ca.cavidades }} {{ ca.cavidades === 1 ? 'cav.' : 'cavs.' }}</span>
          </div>
        </div>

        <div v-if="item.status !== 2 && isCazel && item.piezasScrap > 0" class="info-row">
          <span class="info-key">
            <v-icon size="12" color="#8A95A3">mdi-alert-circle-outline</v-icon>
            Piezas No OK
          </span>
          <span class="info-val">{{ item.piezasScrap != null ? item.piezasScrap : 0 }} pzas</span>
        </div>
      </div>

      <!-- BLOQUE DE PARO -->
      <div v-if="showParoBlock" class="paro-section" :class="{ 'paro-np': item.status === 4 }">
        <p class="paro-title">Paro en curso</p>
        <div class="paro-timer">
          <span class="paro-timer-hms">{{ timerHMS }}</span><span class="paro-timer-ms">.{{ timerMS }}</span>
        </div>
        <!-- Inicio, Piezas y OT repartidos a lo ancho de la fila; si no caben, la OT baja de renglón. -->
        <div class="paro-meta">
          <span>Inicio: <b class="paro-meta-dato">{{ item.inicioParo || '—' }}</b></span>
          <span>Piezas: <b class="paro-meta-dato">{{ piezasOtTxt }}</b></span>
          <span v-if="otTxt">OT: <b class="paro-meta-dato">{{ otTxt }}</b></span>
        </div>
        <div v-if="item.paroTipo" class="paro-tag" :class="item.status === 4 ? 'tag-red' : 'tag-amber'">
          {{ item.paroTipo }}<template v-if="item.paroSubTipo"> · {{ item.paroSubTipo }}</template>
        </div>
      </div>

      <!-- ACTIVA SIN OT: piezas producidas que ninguna OT recibió. La ventana son las últimas 24 h
           (el default de /ReportesMoldes/PiezasSinAsignar, mismo criterio que el badge de ops), no
           "desde la última OT" como decía antes esta etiqueta. -->
      <div v-if="item.activaSinOt" class="sin-asignar-section">
        <div class="sin-asignar-box">
          <div class="sin-asignar-label">
            <v-icon size="13" color="#1257C7">mdi-package-variant-closed</v-icon>
            Piezas sin asignar
          </div>
          <span class="sin-asignar-value">
            <v-progress-circular v-if="item.piezasSinAsignarCargando" indeterminate size="20" width="2" color="#1257C7" />
            <template v-else>{{ piezasSinAsignarTxt }}</template>
          </span>
          <span class="sin-asignar-hint">últimas 24 h</span>
        </div>
      </div>

      <!-- ACTIVA SIN OT: la OT que el plan teórico (Plan de Producción V4) tenía programada para
           este momento, es decir la que debería estar en curso: su ventana teórica cruza la línea
           de "ahora" pero la máquina produce sin que nadie la haya arrancado. Solo en la tarjeta
           completa (versión no compactada). -->
      <div v-if="mostrarOtProgramada" class="ot-prog-section">
        <div class="ot-prog-box">
          <div class="ot-prog-head">
            <span class="ot-prog-label">
              <v-icon size="13" color="#B36B00">mdi-clipboard-text-clock-outline</v-icon>
              OT programada ahora
            </span>
            <span v-if="otProg && otProg.otCritica" class="ot-prog-critica">Crítica</span>
          </div>

          <div v-if="item.otProgramadaCargando" class="ot-prog-loading">
            <v-progress-circular indeterminate size="20" width="2" color="#B36B00" />
          </div>

          <template v-else-if="otProg">
            <!-- Jerarquía: el molde es el dato que decide el arranque, así que va como título.
                 Si la OT no resuelve molde, el título lo toma la clave y no se repite abajo. -->
            <span class="ot-prog-molde">{{ otProgTitulo }}</span>
            <span v-if="otProgSubtitulo" class="ot-prog-clave">OT {{ otProgSubtitulo }}</span>

            <!-- Tiempo sin arrancar contra el lapso que el plan le dio a la OT. -->
            <div class="ot-prog-atraso">
              <div class="ot-prog-atraso-row">
                <span class="ot-prog-atraso-label">{{ otProg.iniciada ? 'Sin reportar' : 'Sin arrancar' }}</span>
                <span class="ot-prog-atraso-val">
                  {{ otProgAtrasoTxt }}
                  <span class="ot-prog-atraso-total">de {{ otProgVentanaTotalTxt }} prog.</span>
                </span>
              </div>
              <div class="ot-prog-bar">
                <div class="ot-prog-bar-fill" :style="{ width: otProgAtrasoPct + '%' }"></div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- PRODUCCIÓN (status activo). En la versión compacta los cuadros son totales de la
           OT completa (producidas, no conformes y programadas), no la producción del turno. -->
      <div v-if="item.status === 1 && !item.activaSinOt" class="prod-section">
        <div v-if="datosPiezas.ok" class="prod-box">
          <span class="prod-box-label">Pzas. Ok</span>
          <span class="prod-box-value">{{ compacta ? okOtTxt : item.pizasOk }}</span>
        </div>
        <div v-if="datosPiezas.noOk && (isCazel || compacta)" class="prod-box prod-box-no-ok">
          <span class="prod-box-label">Pzas. No OK</span>
          <span class="prod-box-value">
            {{ compacta ? noOkOtTxt : (item.piezasScrap != null ? item.piezasScrap : 0) }}
          </span>
        </div>
        <div v-if="datosPiezas.porOt && !isCazel && !compacta" class="prod-box">
          <span class="prod-box-label">Pzas. x OT</span>
          <span class="prod-box-value blue">{{ item.pizasOkPorOrden }}</span>
        </div>
        <div v-if="datosPiezas.programadas" class="prod-box">
          <span class="prod-box-label">Pzas. Prog.</span>
          <span class="prod-box-value">{{ compacta ? programadasOtTxt : item.planeacionPorTurno }}</span>
        </div>
        <div v-if="datosPiezas.rate && !compacta" class="prod-box">
          <span class="prod-box-label">Pzas. RATE</span>
          <span class="prod-box-value">{{ metaTxt(item.piezasRate) }}</span>
        </div>
      </div>

    </template>

    <template v-else>
      <div class="inactive-state">
        <div class="inactive-icon">
          <v-icon size="22" color="#8A95A3">{{ item.otSinGolpes ? 'mdi-pause-circle-outline' : 'mdi-minus-circle-outline' }}</v-icon>
        </div>
        <!-- OT abierta sin golpes: el contador no la reporta activa, así que no cuenta como Activa. -->
        <template v-if="item.otSinGolpes">
          <p class="inactive-text">OT sin golpes</p>
          <p class="inactive-sub">
            El contador no reporta actividad<template v-if="otTxt"> · OT {{ otTxt }}</template>
          </p>
        </template>
        <p v-else class="inactive-text">Sin OT</p>
      </div>
    </template>

  </div>
</template>

<script>
import moment from "moment";
import ICountUp from "vue-countup-v2";
import { mapState } from 'vuex';

const R = 36;
const CIRCUMFERENCE = 2 * Math.PI * R;

const STATUS_MAP = {
  1: { label: 'Activa',          color: '#2B7EFF' },
  2: { label: 'Sin OT',          color: '#8A95A3' },
  3: { label: 'Paro Programado', color: '#FF9F00' },
  4: { label: 'Paro No Prog.',   color: '#E83535' },
};

export default {
  name: 'CardMaquina',
  components: { ICountUp },

  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
    // True cuando la tarjeta se abre desde la versión compacta (diálogo de detalle):
    // oculta el TC Cotizado y renombra "TC Planeación" a "TC Programado".
    compacta: {
      type: Boolean,
      default: false,
    },
    // Configuración "Agregar LH y RH": añade el lado después del código del artículo.
    ladoLhRh: {
      type: Boolean,
      default: false,
    },
    // Nombre configurable del paro programado (status 3). Debe coincidir con el de la pestaña.
    nombreParoProg: {
      type: String,
      default: 'Paro Programado',
    },
    // Configuración de qué datos de piezas mostrar: { ok, noOk, porOt, programadas, rate }
    datosPiezas: {
      type: Object,
      default: () => ({ ok: true, noOk: true, porOt: true, programadas: true, rate: true }),
    },
  },

  data: () => ({
    segundos: 0,
    milisegundos: 0,
    interval: null,
    intervalMs: null,
  }),

  watch: {
    'item._segundosInicialesParo': 'resetTimer',
    'item.fechaInicio': 'resetTimer',
    'item.status': 'resetTimer',
  },

  computed: {
    ...mapState({ nombreEmpresa: 'nombreEmpresa' }),

    isCazel() {
      const n = this.nombreEmpresa || '';
      return n === 'Cazel' || n === 'CAZEL-TLT' || n === 'LOGISTORAGE';
    },

    inactiveCode() {
      return this.isCazel ? 2 : 0;
    },

    isInactive() {
      return this.isCazel && this.item?.status === this.inactiveCode;
    },

    totalPiezas() {
      return (Number(this.item?.pizasOk) || 0) + (Number(this.item?.piezasScrap) || 0);
    },

    piezasSinAsignarTxt() {
      const n = Number(this.item?.piezasSinAsignar);
      return Number.isFinite(n) ? n.toLocaleString('es-MX') : '0';
    },

    // Clave de la OT para el bloque de paro ('' si la máquina no trae OT).
    otTxt() {
      const ot = this.limpiarOT(this.item?.ot);
      return ot && ot !== '-' ? ot : '';
    },

    // Piezas Ok de la OT = producidas totales menos las no conformes. Mismo criterio que el
    // tablero V4: si el no OK excediera el total, se recorta a 0.
    piezasOkOt() {
      const total = Number(this.item?.piezasHechasOt) || 0;
      const noOk = Number(this.item?.piezasNoOkOt) || 0;
      return Math.max(0, total - noOk);
    },

    okOtTxt() {
      return this.metaTxt(this.piezasOkOt);
    },

    noOkOtTxt() {
      return this.metaTxt(this.item?.piezasNoOkOt);
    },

    // Programadas de la OT completa (suma de todas sus operaciones), no del turno.
    programadasOtTxt() {
      return this.metaTxt(this.item?.piezasProgramadasOt);
    },

    // Avance de la OT en el bloque de paro: OK / programadas. Solo cuentan las piezas OK, no el
    // total producido, para que el avance no se infle con las no conformes.
    piezasOtTxt() {
      return `${this.metaTxt(this.piezasOkOt)}/${this.metaTxt(this.item?.piezasProgramadasOt)}`;
    },

    /* ────────────────────────────────────────
       OT PROGRAMADA AHORA (solo activa sin OT)
       Viene de /ReportesMoldes/OTProgramadaAhora: la barra teórica del Plan de Producción V4 cuya
       ventana [inicio, fin) cruza "ahora". El back solo devuelve fila si hay plan vigente, así que
       otProgramada = null significa "esta máquina no tenía nada programado para este momento".
    ──────────────────────────────────────── */
    otProg() {
      const raw = this.item?.otProgramada;
      if (!raw || typeof raw !== 'object') return null;
      const g = n => this.ciVal(raw, n);
      // El endpoint trae además artículos, piezas y TC; la tarjeta solo pinta molde, clave y el
      // tiempo que lleva sin arrancar contra lo programado.
      return {
        clave: this.limpiarOT(g('clave')) || '—',
        molde: g('molde') || '',
        inicio: g('inicio') || null,
        fin: g('fin') || null,
        atrasoSegundos: Number(g('atrasoSegundos')) || 0,
        // Lapso que el plan le dio a la OT = duración base + holgura (es el ancho de la ventana).
        duracionSegundos: Number(g('duracionTeoricaSegundos')) || 0,
        holguraSegundos: Number(g('holguraSegundos')) || 0,
        // La OT ya tuvo producción real: se arrancó antes y se dejó de reportar.
        iniciada: g('iniciada') === true,
        otCritica: g('otCritica') === true,
      };
    },

    // El bloque es exclusivo de la tarjeta completa (versión no compactada) y de las máquinas
    // activas sin OT. Mientras la consulta no resuelve se muestra con su propio loading.
    mostrarOtProgramada() {
      if (this.compacta || !this.item?.activaSinOt) return false;
      return !!this.item?.otProgramadaCargando || !!this.otProg;
    },

    // "S/D" es el placeholder del back cuando la OT no resuelve molde: no se pinta.
    otProgMolde() {
      const molde = this.otProg?.molde;
      return molde && molde !== 'S/D' ? molde : '';
    },

    // El molde manda: es el dato que decide el arranque, así que encabeza el bloque. Sin molde el
    // título lo toma la clave de la OT (y entonces no se repite en el subtítulo).
    otProgTitulo() {
      return this.otProgMolde || this.otProg?.clave || '—';
    },

    otProgSubtitulo() {
      return this.otProgMolde ? (this.otProg?.clave || '') : '';
    },

    // Lapso completo que el plan le dio a la OT (base + holgura). Si no viene, se deduce de la
    // ventana [inicio, fin).
    otProgVentanaSeg() {
      const p = this.otProg;
      if (!p) return 0;
      const total = (Number(p.duracionSegundos) || 0) + (Number(p.holguraSegundos) || 0);
      if (total > 0) return total;
      const ini = p.inicio ? moment(p.inicio) : null;
      const fin = p.fin ? moment(p.fin) : null;
      if (!ini?.isValid() || !fin?.isValid()) return 0;
      return Math.max(0, fin.diff(ini, 'seconds'));
    },

    otProgVentanaTotalTxt() {
      return this.duracionTxt(this.otProgVentanaSeg);
    },

    // Qué parte del lapso programado ya se consumió sin arrancar. Alimenta la barra del bloque.
    otProgAtrasoPct() {
      const total = this.otProgVentanaSeg;
      if (!(total > 0)) return 0;
      const pct = (Number(this.otProg?.atrasoSegundos) || 0) * 100 / total;
      return Math.min(100, Math.max(0, Math.round(pct)));
    },

    otProgAtrasoTxt() {
      return this.duracionTxt(this.otProg?.atrasoSegundos);
    },

    showOeeSection() {
      // No mostrar OEE si la máquina está activa sin OT (no hay datos válidos)
      if (this.item?.activaSinOt) return false;
      // Mostrar OEE en todas las demás situaciones
      return true;
    },

    showTcRow() {
      // Mostrar TC solo en máquinas activas
      return this.item?.status === 1;
    },

    showCavidades() {
      return this.item?.status >= 1
        && Array.isArray(this.item?.cavidadesArticulos)
        && this.item.cavidadesArticulos.length > 0;
    },

    mostrarSegundero() {
      return this.item?.status === 3 || this.item?.status === 4;
    },

    showParoBlock() {
      return this.isCazel ? this.item.status > 2 : this.item.status >= 2;
    },

    timerHMS() {
      const s = this.mostrarSegundero ? this.segundos : (this.item.tiempoParoSegundos || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = n => String(n).padStart(2, '0');
      return h > 0 ? pad(h) + ':' + pad(m) + ':' + pad(sec) : pad(m) + ':' + pad(sec);
    },

    timerMS() {
      return String(this.milisegundos).padStart(2, '0');
    },

    circumference() {
      return CIRCUMFERENCE;
    },

    oeeArc() {
      const val = Math.min(100, Math.max(0, Number(this.item?.oee ?? 0)));
      return ((val / 100) * CIRCUMFERENCE).toFixed(2);
    },

    kpis() {
      const fmt = v => {
        const n = Number(v);
        return Number.isFinite(n) && n != null ? n.toFixed(1) + '%' : '—';
      };
      const disp = Number(this.item?.disponibilidad ?? null);
      const rend = Number(this.item?.rendimiento ?? null);
      const cal = Number(this.item?.calidad ?? null);
      return [
        { name: 'Disponib.',  val: Number.isFinite(disp) ? Math.min(100, Math.max(0, disp)) : 0, color: '#A855F7', display: fmt(this.item?.disponibilidad) },
        { name: 'Eficiencia', val: Number.isFinite(rend) ? Math.min(100, Math.max(0, rend)) : 0, color: '#10B981', display: fmt(this.item?.rendimiento)    },
        { name: 'Calidad',    val: Number.isFinite(cal) ? Math.min(100, Math.max(0, cal)) : 0, color: '#06B6D4', display: fmt(this.item?.calidad)        },
      ];
    },

    cardBorderStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        border: `1.5px solid ${c}30`,
        boxShadow: `0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px ${c}15`,
      };
    },

    pillStyle() {
      const c = this.colorEstatus(this.item?.status);
      return {
        color: c,
        background: c + '14',
        borderColor: c + '40',
      };
    },
  },

  created() {
    this.resetTimer();
    this.iniciar();
    this.iniciarMs();
  },

  destroyed() {
    this.detener();
    this.detenerMs();
  },

  methods: {
    resetTimer() {
      try {
        if (this.item._segundosInicialesParo > 0) {
          this.segundos = this.item._segundosInicialesParo;
        } else if (this.item.fechaInicio) {
          const diff = moment().valueOf() - moment(this.item.fechaInicio).valueOf();
          this.segundos = Math.max(0, Math.trunc(diff / 1000));
          if (isNaN(this.segundos)) this.segundos = 0;
        } else {
          this.segundos = 0;
        }
      } catch {
        this.segundos = 0;
      }
    },

    colorEstatus(status) {
      return (STATUS_MAP[status] || STATUS_MAP[2]).color;
    },

    labelEstatus(status) {
      if (status === 3) return this.nombreParoProg;
      return (STATUS_MAP[status] || STATUS_MAP[2]).label;
    },

    limpiarOT(valor) {
      if (!valor) return '';
      return valor.replace(/^OT:\s*/i, '');
    },

    // El adaptador determina el lado exclusivamente desde la descripción del artículo.
    // Si no hubo una coincidencia inequívoca, `lado` llega vacío y se deja solo el código.
    sufijoLado(lado) {
      if (!this.ladoLhRh || (lado !== 'LH' && lado !== 'RH')) return '';
      return ` ${lado}`;
    },

    // Lectura de propiedad sin distinguir mayúsculas: el back tiene doble registro de serializador,
    // así que la misma llave puede llegar en PascalCase o camelCase.
    ciVal(obj, name) {
      if (!obj || typeof obj !== 'object') return undefined;
      const key = Object.keys(obj).find(k => k.toLowerCase() === String(name).toLowerCase());
      return key === undefined ? undefined : obj[key];
    },

    // Duración legible a partir de segundos: "45 seg", "12 min", "2 h 15 min".
    duracionTxt(segundos) {
      const s = Math.max(0, Math.round(Number(segundos) || 0));
      if (s < 60) return `${s} seg`;
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      if (h <= 0) return `${m} min`;
      return m > 0 ? `${h} h ${m} min` : `${h} h`;
    },

    // Meta de piezas del artículo, con separador de miles y sin decimales.
    metaTxt(valor) {
      const n = Number(valor);
      return Number.isFinite(n) ? Math.round(n).toLocaleString('es-MX') : '0';
    },

    validTcText(value) {
      return value != null && value !== '' && value !== '—' && value !== 'â€”';
    },

    tcDisplay(value) {
      return this.validTcText(value) ? value : '—';
    },

    iniciar() {
      if (this.interval) return;
      this.interval = setInterval(() => {
        this.segundos += 1;
        if (!this.isCazel) return;
        const tcm = Number(this.item?.tiempoCambioMoldeSeg);
        if (!Number.isFinite(tcm) || tcm <= 0) return;
        if (this.item.status === 2 && this.segundos >= tcm) {
          this.$emit('status', 3);
          return;
        }
        if (this.item.status === 3 && this.segundos >= tcm + 15 * 60) {
          this.$emit('status', 4);
        }
      }, 1000);
    },

    detener() {
      if (!this.interval) return;
      clearInterval(this.interval);
      this.interval = null;
    },

    iniciarMs() {
      if (this.intervalMs) return;
      this.intervalMs = setInterval(() => {
        if (!this.mostrarSegundero) return;
        this.milisegundos = (this.milisegundos + 1) % 100;
      }, 10);
    },

    detenerMs() {
      if (!this.intervalMs) return;
      clearInterval(this.intervalMs);
      this.intervalMs = null;
    },
  },
};
</script>

<style scoped>
/* ===================================================
   TOKENS
   =================================================== */
.card-maquina {
  --radius-card: 20px;
  --radius-inner: 12px;
  --radius-sm: 8px;
  --clr-bg: #F4F6F9;
  --clr-surface: #F0F2F6;
  --clr-border: rgba(0,0,0,0.07);
  --clr-text-primary: #0D1117;
  --clr-text-secondary: #5A6476;
  --clr-text-muted: #8A95A3;
  --font-primary: 'Space Grotesk', 'Roboto', sans-serif;
  --font-body: 'DM Sans', 'Roboto', sans-serif;
}

/* ===================================================
   CARD BASE
   =================================================== */
.card-maquina {
  background: #FFFFFF;
  border-radius: var(--radius-card);
  position: relative;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font-family: var(--font-body);
  display: flex;
  flex-direction: column;
}

.card-maquina:hover {
  transform: translateY(-3px);
}

.side-status-bar {
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  opacity: 0.92;
}

/* ===================================================
   HEADER
   =================================================== */
.card-header {
  padding: 16px 20px 14px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--clr-border);
  flex-shrink: 0;
}

.machine-id-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.machine-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
}

.machine-code {
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 700;
  color: #2B7EFF;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

/* Molde en el encabezado: negro y del mismo tamaño que el código de máquina. */
.header-molde {
  flex: 0 1 auto;
  min-width: 0;
  padding: 0 8px;
  font-family: var(--font-primary);
  font-size: 22px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border: 1.5px solid;
  transition: background 0.3s, color 0.3s;
}

.status-led {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  flex-shrink: 0;
}

/* ===================================================
   OEE RING + KPIS
   =================================================== */
.oee-section {
  padding: 18px 20px 12px;
  display: flex;
  align-items: center;
  gap: 18px;
}

.ring-wrap {
  position: relative;
  width: 90px;
  height: 90px;
  flex-shrink: 0;
}

.ring-wrap svg circle:last-child {
  transition: stroke-dasharray 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.ring-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  line-height: 1;
  pointer-events: none;
}

.ring-value {
  font-family: var(--font-primary);
  font-size: 19px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
}

.ring-pct {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
}

.ring-sub {
  display: block;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-top: 2px;
  font-family: var(--font-primary);
}

/* KPI rows */
.kpi-trio {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.kpi-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kpi-label-block {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 76px;
}

.kpi-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.kpi-name {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-secondary);
}

.kpi-bar-wrap {
  flex: 1;
  height: 5px;
  background: var(--clr-surface);
  border-radius: 10px;
  overflow: hidden;
}

.kpi-bar-fill {
  height: 100%;
  border-radius: 10px;
  opacity: 0.75;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.kpi-value {
  font-family: var(--font-primary);
  font-size: 11px;
  font-weight: 600;
  color: var(--clr-text-primary);
  min-width: 40px;
  text-align: right;
}

/* ===================================================
   TC CHIPS
   =================================================== */
.tc-row {
  padding: 0 20px 14px;
  display: flex;
  gap: 8px;
}

.tc-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 8px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  flex: 1;
  justify-content: center;
}

.tc-chip-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  font-family: var(--font-primary);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.tc-chip-value {
  font-size: 12px;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--clr-text-primary);
}

.tc-chip-value.qad {
  color: #2B7EFF;
}

/* ===================================================
   DIVIDER
   =================================================== */
.h-divider {
  height: 1px;
  background: var(--clr-border);
  margin: 0 20px;
  flex-shrink: 0;
}

/* ===================================================
   INFO SECTION
   =================================================== */
.info-section {
  padding: 10px 20px 4px;
}

.info-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(0,0,0,0.04);
}

.info-row:last-child { border-bottom: none; }

.info-key {
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-muted);
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.info-key svg { opacity: 0.7; }

.info-val {
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  text-align: right;
  max-width: 58%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* La OT es el dato azul de la tarjeta (el molde va en negro). */
.ot-val {
  color: #2B7EFF;
  font-weight: 700;
}

/* ===================================================
   CAVIDADES POR ARTÍCULO
   =================================================== */
.cavidades-block {
  margin: 8px 0 4px;
  background: var(--clr-surface);
  border: 1px solid var(--clr-border);
  border-radius: var(--radius-inner);
  padding: 10px 12px;
}

/* Grid de 5 columnas: punto, artículo, meta, hueco de contrapeso y badge. Los dos 1fr a los
   lados de la meta la dejan centrada en la fila, no en el espacio que sobra a la derecha. */
.cavidades-item {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
}

.cavidades-item + .cavidades-item {
  border-top: 1px dashed rgba(0,0,0,0.07);
}

.cavidades-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2B7EFF;
  opacity: 0.65;
  flex-shrink: 0;
}

.cavidades-art {
  min-width: 0;
  font-family: var(--font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--clr-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Meta de piezas del artículo: centrada entre el código y el badge de cavidades. */
.cavidades-meta {
  grid-column: 3;
  text-align: center;
  font-family: var(--font-primary);
  font-size: 13px;
  font-weight: 700;
  color: var(--clr-text-secondary);
  white-space: nowrap;
}

.cavidades-badge {
  /* Columna 5: la 4 queda vacía y hace de contrapeso para centrar la meta. */
  grid-column: 5;
  justify-self: end;
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  border-radius: 20px;
  background: rgba(43, 126, 255, 0.10);
  color: #2B7EFF;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* ===================================================
   PARO SECTION
   =================================================== */
.paro-section {
  margin: 10px 20px 4px;
  background: var(--clr-surface);
  border-radius: var(--radius-inner);
  padding: 13px 15px;
  border-left: 3px solid #FF9F00;
  flex-shrink: 0;
}

.paro-section.paro-np {
  border-left-color: #E83535;
}

.paro-title {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  margin-bottom: 6px;
}

.paro-timer {
  font-family: var(--font-primary);
  font-size: 26px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 5px;
}

.paro-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  /* space-between reparte el sobrante a lo ancho: los 3 datos quedan equidistantes. */
  justify-content: space-between;
  gap: 2px 10px;
  font-size: 11px;
  color: var(--clr-text-secondary);
  line-height: 1.4;
  /* Antes era un <p>: repone el margen que aportaba el reset de Vuetify. */
  margin-bottom: 8px;
}

/* Etiquetas en gris, valores en negritas: aplica igual a las piezas y a la clave de OT. */
.paro-meta-dato {
  font-family: var(--font-primary);
  font-weight: 700;
  color: var(--clr-text-primary);
}

.paro-tag {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  font-family: var(--font-primary);
  margin-top: 8px;
  letter-spacing: 0.03em;
  border: 1px solid;
}

.tag-amber { color: #FF9F00; background: rgba(255,159,0,0.10); border-color: rgba(255,159,0,0.30); }
.tag-red   { color: #E83535; background: rgba(232,53,53,0.10);  border-color: rgba(232,53,53,0.30);  }

/* ===================================================
   PRODUCCIÓN
   =================================================== */
.prod-section {
  padding: 12px 20px 18px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 8px;
  flex-shrink: 0;
}

.prod-box {
  background: var(--clr-surface);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.prod-box-no-ok {
  background: var(--clr-surface);
  border: none;
}

.prod-box-label {
  font-size: 10px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: var(--font-primary);
}

.prod-box-value {
  font-family: var(--font-primary);
  font-size: 20px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.prod-box-value.blue   { color: #2B7EFF; }

/* ===================================================
   INACTIVO
   =================================================== */
.inactive-state {
  padding: 32px 20px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.inactive-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--clr-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: var(--clr-text-muted);
}

.inactive-text {
  font-family: var(--font-primary);
  font-size: 14px;
  font-weight: 600;
  color: var(--clr-text-secondary);
}

.inactive-sub {
  font-size: 12px;
  color: var(--clr-text-muted);
  margin-top: 3px;
}

/* ===================================================
   ANIMACIÓN DE ENTRADA
   =================================================== */
@keyframes card-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.card-maquina {
  animation: card-in 0.4s ease both;
}

/* ===================================================
   RESPONSIVE
   =================================================== */
@media (max-width: 600px) {
  .machine-code { font-size: 18px; }
  .ring-value   { font-size: 16px; }
  .paro-timer   { font-size: 22px; }
  .prod-box-value { font-size: 18px; }
  .oee-section  { padding: 14px 16px 10px; }
  .card-header  { padding: 14px 16px 12px; }
  .tc-row       { padding: 0 16px 12px; }
}

@media (min-width: 1265px) {
  .machine-code { font-size: 24px; }
  .ring-value   { font-size: 20px; }
  .kpi-name     { font-size: 12px; }
  .kpi-value    { font-size: 12px; }
  .prod-box-value { font-size: 22px; }
}

/* Pantalla 65" CAZEL */
@media screen and (min-width: 3200px) {
  .machine-code    { font-size: 2.8rem; }
  .ring-wrap       { width: 120px; height: 120px; }
  .ring-wrap svg   { width: 120px; height: 120px; }
  .ring-value      { font-size: 26px; }
  .ring-sub        { font-size: 12px; }
  .paro-timer      { font-size: 2.2rem; }
  .prod-box-value  { font-size: 1.8rem; }
  .kpi-name        { font-size: 14px; }
  .kpi-value       { font-size: 14px; }
  .info-key        { font-size: 14px; }
  .info-val        { font-size: 15px; }
  .status-pill     { font-size: 14px; padding: 8px 16px; }
  .tc-chip-label   { font-size: 13px; }
  .tc-chip-value   { font-size: 16px; }
  .prod-box-label  { font-size: 13px; }
  .cavidades-art   { font-size: 15px; }
  .cavidades-badge { font-size: 13px; padding: 3px 12px; }
}

@media (hover: none) {
  .card-maquina:hover { transform: none; }
}

/* ===================================================
   HEADER RIGHT + machine-label-row
   =================================================== */
.machine-label-row {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 2px;
}

.machine-cog-icon {
  animation: machine-cog-spin 2.8s linear infinite;
}

@keyframes machine-cog-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

/* ===================================================
   BADGE "ACTIVA SIN OT"
   =================================================== */
.activa-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 30px;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: #2B7EFF;
  background: rgba(43, 126, 255, 0.10);
  border: 1.5px solid rgba(43, 126, 255, 0.30);
  white-space: nowrap;
}

.activa-led {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2B7EFF;
  flex-shrink: 0;
  animation: activa-pulse 1.6s ease-in-out infinite;
}

/* Variante del badge de cabecera: OT sin golpes (gris). Comparte forma y led con "Activa sin OT". */
.activa-badge.badge-gray  { color: #8A95A3; background: rgba(138,149,163,0.12); border-color: rgba(138,149,163,0.35); }
.activa-badge.badge-gray .activa-led  { background: #8A95A3; animation: none; }

@keyframes activa-pulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
}

/* ===================================================
   ACTIVA SIN OT: PIEZAS SIN ASIGNAR
   =================================================== */
.sin-asignar-section {
  padding: 12px 20px 18px;
  flex-shrink: 0;
}

.sin-asignar-box {
  background: rgba(43, 126, 255, 0.07);
  border: 1px solid rgba(43, 126, 255, 0.22);
  border-radius: var(--radius-inner);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sin-asignar-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
}

.sin-asignar-value {
  font-family: var(--font-primary);
  font-size: 26px;
  font-weight: 700;
  color: #1257C7;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.sin-asignar-hint {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-secondary);
}

@media screen and (min-width: 3200px) {
  .sin-asignar-label { font-size: 13px; }
  .sin-asignar-value { font-size: 2.2rem; }
  .sin-asignar-hint  { font-size: 14px; }
}

/* ===================================================
   ACTIVA SIN OT: OT PROGRAMADA AHORA
   Ámbar (no azul como las piezas sin asignar): es un aviso, el plan esperaba esta OT corriendo.
   =================================================== */
.ot-prog-section {
  /* El bloque va pegado al de piezas sin asignar, que ya trae el padding inferior. */
  padding: 0 20px 18px;
  flex-shrink: 0;
}

.ot-prog-box {
  background: rgba(255, 159, 0, 0.08);
  border: 1px solid rgba(255, 159, 0, 0.28);
  border-radius: var(--radius-inner);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ot-prog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ot-prog-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
}

.ot-prog-critica {
  font-family: var(--font-primary);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #E83535;
  background: rgba(232, 53, 53, 0.10);
  border: 1px solid rgba(232, 53, 53, 0.30);
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
}

.ot-prog-loading {
  display: flex;
  justify-content: center;
  padding: 4px 0;
}

/* Molde: el dato más notable de todo el bloque. */
.ot-prog-molde {
  font-family: var(--font-primary);
  font-size: 27px;
  font-weight: 700;
  color: #B36B00;
  letter-spacing: -0.03em;
  line-height: 1.05;
  word-break: break-all;
}

/* Clave de la OT: segundo nivel, debajo del molde. */
.ot-prog-clave {
  font-family: var(--font-primary);
  font-size: 13px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.01em;
  line-height: 1.2;
  word-break: break-all;
}

/* Tiempo sin arrancar vs. lapso programado: tercer nivel. */
.ot-prog-atraso {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 3px;
}

.ot-prog-atraso-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.ot-prog-atraso-label {
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--clr-text-muted);
  white-space: nowrap;
}

.ot-prog-atraso-val {
  font-family: var(--font-primary);
  font-size: 15px;
  font-weight: 700;
  color: #B36B00;
  letter-spacing: -0.01em;
  text-align: right;
}

.ot-prog-atraso-total {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-text-secondary);
  margin-left: 3px;
  white-space: nowrap;
}

.ot-prog-bar {
  height: 4px;
  border-radius: 4px;
  background: rgba(255, 159, 0, 0.20);
  overflow: hidden;
}

.ot-prog-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: #FF9F00;
  transition: width 0.4s ease;
}

@media screen and (min-width: 3200px) {
  .ot-prog-label       { font-size: 13px; }
  .ot-prog-molde       { font-size: 2.4rem; }
  .ot-prog-clave       { font-size: 17px; }
  .ot-prog-atraso-label{ font-size: 13px; }
  .ot-prog-atraso-val  { font-size: 1.2rem; }
  .ot-prog-atraso-total{ font-size: 14px; }
  .ot-prog-bar         { height: 6px; }
  .ot-prog-critica     { font-size: 12px; }
}

/* ===================================================
   PARO TIMER CON MILISEGUNDOS
   =================================================== */
.paro-timer {
  display: flex;
  align-items: baseline;
  gap: 1px;
  font-family: var(--font-primary);
  line-height: 1;
  margin-bottom: 5px;
}

.paro-timer-hms {
  font-size: 26px;
  font-weight: 700;
  color: var(--clr-text-primary);
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}

.paro-timer-ms {
  font-size: 16px;
  font-weight: 500;
  color: var(--clr-text-muted);
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
}

</style>
